import {
  DateFormat,
  DEFAULT_DATE_FORMAT,
  DEFAULT_TIME_FORMAT,
  TimeFormat,
  formatDateTime,
} from '../i18n/date-format';
import { ProjectInfo } from '../models/project-info';
import {
  BUILT_IN_COLUMNS,
  DEFAULT_PROJECT_GRID_VIEW,
  LEGACY_KNOWN_COLUMNS,
  builtInColumn,
  isUserFieldColumn,
  ProjectGridColumnDef,
  ProjectGridModel,
  ProjectGridRow,
  ProjectGridView,
  StatusFilter,
  USER_FIELD_COLUMN_WIDTH,
  userFieldColumnId,
  userFieldName,
  ProjectGridEnumOption,
} from '../models/project-grid.types';
import {
  byNewestTelemetry,
  lastTelemetryEpoch,
  partitionProjects,
} from './project-liveness';

/**
 * The project grid's view model: filter, order, section and render — ONCE per data
 * change, not once per change-detection cycle.
 *
 * WHY THIS IS A PURE MODULE. The pane computed everything in component getters, so
 * `getFilteredProjects()` filtered AND sorted a fresh array on every call and the
 * template called it six or more times per cycle; `getAllUserParameterNames()`
 * rescanned every project once per header cell AND once per body cell. None of
 * that was fixable by adopting a grid library, because a library would call the
 * same getters. It is fixed by computing the model once and binding to the result,
 * which also makes every rule below assertable without a DOM.
 */

/** What the user has narrowed the list to. Separate from the saved view: transient. */
export interface ProjectGridCriteria {
  searchTerm: string;
  statusFilter: StatusFilter;
  dateFrom: string;
  dateTo: string;
}

export const EMPTY_CRITERIA: ProjectGridCriteria = {
  searchTerm: '',
  statusFilter: 'all',
  dateFrom: '',
  dateTo: '',
};

/**
 * Every user-defined field name present across the given projects, sorted.
 *
 * Computed ONCE per project list here, rather than per cell in the template.
 */
export function userFieldNames(projects: readonly ProjectInfo[]): string[] {
  const names = new Set<string>();
  for (const project of projects ?? []) {
    for (const param of project.UserParameter ?? []) {
      if (param?.Name) names.add(param.Name);
    }
  }
  return Array.from(names).sort();
}

/**
 * The columns this caller could actually choose from: every built-in, plus one per
 * user-defined field their projects carry.
 */
export function availableColumns(projects: readonly ProjectInfo[]): ProjectGridColumnDef[] {
  return [
    // A column marked deriveOptions gets its vocabulary from the loaded rows. Done
    // HERE because this is where a column definition meets a dataset, so both
    // consumers of the grid get it without either remembering to.
    ...BUILT_IN_COLUMNS.map((column) =>
      column.deriveOptions
        ? { ...column, enumOptions: deriveEnumOptions(projects, column.id) }
        : column
    ),
    ...userFieldNames(projects).map((name) => ({
      id: userFieldColumnId(name),
      label: name,
      type: 'text' as const,
      width: USER_FIELD_COLUMN_WIDTH,
    })),
  ];
}

/**
 * Drop columns that do not exist for this caller, then re-prune anything that
 * referenced them.
 *
 * THE ONE NORMALISATION STEP THE SERVER CANNOT DO. DataServices resolves and
 * normalises the three tiers, but it does not know which user-defined fields a
 * given caller's projects carry without loading the project list, so a view naming
 * `up:Pipe Size` cannot be validated there. Everything else — the merge order, the
 * required column, the depth cap — stays server-side so there is one implementation
 * of it.
 *
 * A view naming a vanished column is a STALE PREFERENCE, not an error: drop it and
 * carry on. Failing the grid would lock a user out of the product with no way back.
 */
export function pruneToAvailableColumns(
  view: ProjectGridView,
  available: readonly ProjectGridColumnDef[]
): ProjectGridView {
  const ids = new Set(available.map((c) => c.id));

  const columns = (view.columns ?? []).filter((id) => ids.has(id));
  // The project name is not optional; a grid of anonymous rows is unusable.
  if (!columns.includes('ProjectName')) columns.unshift('ProjectName');

  const visible = new Set(columns);
  const sort = (view.sort ?? []).filter((s) => visible.has(s.columnId));
  const filters: ProjectGridView['filters'] = {};
  for (const [id, filter] of Object.entries(view.filters ?? {})) {
    if (visible.has(id)) filters[id] = filter;
  }

  return {
    ...view,
    columns,
    sort: sort.length > 0 ? sort : DEFAULT_PROJECT_GRID_VIEW.sort,
    groupBy: (view.groupBy ?? []).filter((id) => visible.has(id)),
    filters,
  };
}

/**
 * The searchable text for one project, built ONCE when the list loads.
 *
 * The old predicate allocated three joined strings per project on every call, and
 * the predicate ran six-plus times per change-detection cycle — so typing in the
 * search box rebuilt every project's parameter text on every keystroke, per cycle.
 *
 * FIELDS ARE FIXED, not user-defined: project name, device name, serial, customer,
 * fiber type, version, and every user parameter's NAME and VALUE. Dates and numeric
 * cure configuration are deliberately excluded — the date filter owns dates, and a
 * number typed into a search box should not silently match a temperature.
 */
export function searchHaystack(project: ProjectInfo): string {
  const parts: string[] = [
    project.ProjectName,
    deviceLabel(project),
    project.SerialNumber,
    project.CustomerName,
    project.FiberType,
    project.Version,
  ];
  for (const param of project.UserParameter ?? []) {
    if (param?.Name) parts.push(param.Name);
    if (param?.Value) parts.push(param.Value);
  }
  return parts.filter(Boolean).join('  ').toLowerCase();
}

/**
 * Does this project match the search box?
 *
 * AND ACROSS TERMS, OR ACROSS FIELDS WITHIN A TERM. The pane used to match the
 * whole string as one substring against each field, so "vortex 54" found nothing
 * unless one field literally contained it. Narrowing by typing more words is what
 * everyone expects a search box to do.
 */
export function matchesSearch(haystack: string, searchTerm: string): boolean {
  const terms = (searchTerm ?? '').toLowerCase().trim().split(/\s+/).filter(Boolean);
  if (terms.length === 0) return true;
  return terms.every((term) => haystack.includes(term));
}

/**
 * The device label: its name when it has one, otherwise its serial.
 *
 * Mirrors DeviceDisplayName.Resolve on the server and displayName() in the admin
 * console. Never blank — an unnamed device is the common case, and an empty column
 * would read as missing data across the whole fleet. The client keeps its own copy
 * because a project can arrive from an older API build with no DeviceName at all.
 */
export function deviceLabel(project: ProjectInfo): string {
  const name = project.DeviceName?.trim();
  return name ? name : project.SerialNumber ?? '';
}

/**
 * Merge built-in columns a saved view has never seen.
 *
 * THE RULE (Joel, 2026-09-05): a saved view records the user's explicit choices.
 * A column that did not exist when the view was saved cannot have been
 * explicitly excluded — so it merges in at its shipped default position and
 * visibility. From then on it is a known column, and hiding it IS an explicit
 * choice that persists.
 *
 * MERGE, NEVER CLOBBER. Nobody's view is reset: their order, their hidden
 * columns, their sort and filters are untouched. The only change is that a
 * genuinely new column appears where it ships.
 *
 * Position comes from the default view: the new column is inserted after the
 * nearest preceding default column that this view actually shows, so it lands
 * beside its intended neighbour even in a heavily reordered view. If none of its
 * predecessors are visible it goes to the front, which is where it would have
 * been relative to everything that is.
 *
 * A new column that is NOT in the default view stays hidden — shipped default
 * visibility is what merges, and hidden is a default too.
 */
export function mergeUnknownColumns(view: ProjectGridView): ProjectGridView {
  const known = new Set(view.knownColumns ?? LEGACY_KNOWN_COLUMNS);
  const unknown = BUILT_IN_COLUMNS.map((c) => c.id).filter((id) => !known.has(id));
  if (!unknown.length) return view;

  const defaults = DEFAULT_PROJECT_GRID_VIEW.columns;
  const columns = [...view.columns];

  for (const id of unknown) {
    if (columns.includes(id)) continue;          // already shown; nothing to do
    const defaultIndex = defaults.indexOf(id);
    if (defaultIndex < 0) continue;              // not visible by default: stays hidden

    let insertAt = 0;
    for (let i = defaultIndex - 1; i >= 0; i--) {
      const predecessor = columns.indexOf(defaults[i]);
      if (predecessor >= 0) {
        insertAt = predecessor + 1;
        break;
      }
    }
    columns.splice(insertAt, 0, id);
  }

  return {
    ...view,
    columns,
    // Now known: a later hide is an explicit choice and will stick.
    knownColumns: BUILT_IN_COLUMNS.map((c) => c.id),
  };
}

/**
 * Is this cell showing a stand-in rather than a value the device actually has?
 *
 * Only Device Name has one: deviceLabel falls back to the serial when a device is
 * unnamed. Kept beside deviceLabel so the fallback and the flag that marks it can
 * never disagree.
 *
 * THE TEST IS "does the label just repeat the serial", NOT "is DeviceName empty".
 * The server ALREADY resolves the fallback (DeviceDisplayName.Resolve) and sends
 * the serial as deviceName for an unnamed device, so the field is never blank and
 * an emptiness check marks nothing — verified live: 72/72 rows arrived with a
 * non-empty deviceName, 4 of them the serial. The empty case is still treated as a
 * stand-in for older API builds that omit the field entirely.
 */
export function isStandInCell(project: ProjectInfo, columnId: string): boolean {
  if (columnId !== 'DeviceName') return false;
  const name = project.DeviceName?.trim();
  if (!name) return true;
  return name.toLowerCase() === (project.SerialNumber ?? '').trim().toLowerCase();
}

/**
 * "Last seen", in the user's chosen date ORDER.
 *
 * This used toLocaleDateString(), which follows the BROWSER's locale — so the
 * same account saw a different order on a work laptop and a home one, and the
 * date-format preference could not affect it at all. The order is now an
 * argument, threaded from the resolved cascade by the caller, which keeps this
 * function pure and testable rather than having it read a service.
 */
export function formatProjectDate(
  project: ProjectInfo,
  dateFormat: DateFormat = DEFAULT_DATE_FORMAT,
  timeFormat: TimeFormat = DEFAULT_TIME_FORMAT
): string {
  const epoch = lastTelemetryEpoch(project);
  if (epoch === null) return '—';
  return formatDateTime(epoch, dateFormat, 'seconds', timeFormat) || '—';
}

/**
 * One cell's text. Absent values render as an em dash, never as an empty cell —
 * the pane has always done that, and a blank cell reads as a rendering fault
 * rather than as "this project has no value here".
 */
export function cellValue(
  project: ProjectInfo,
  columnId: string,
  dateFormat: DateFormat = DEFAULT_DATE_FORMAT,
  timeFormat: TimeFormat = DEFAULT_TIME_FORMAT
): string {
  if (isUserFieldColumn(columnId)) {
    return project.getUserParameterValue(userFieldName(columnId)) || '—';
  }

  const p = project as unknown as Record<string, unknown>;
  switch (columnId) {
    case 'ProjectName':           return project.ProjectName || '—';
    case 'DeviceName':            return deviceLabel(project) || '—';
    case 'SerialNumber':          return project.SerialNumber || '—';
    case 'LastTelemetryReceived': return formatProjectDate(project, dateFormat, timeFormat);
    case 'CustomerName':          return project.CustomerName || '—';
    case 'FiberType':             return project.FiberType || '—';
    case 'Version':               return project.Version || '—';
    case 'Status':                return project.isLive === true ? 'Live' : 'Historical';
    // Unknown is a dash, not "No": a server that did not say is not a cure that
    // is still open.
    case 'Finalized':             return project.Finalized === true ? 'Yes'
                                       : project.Finalized === false ? 'No' : '—';
    case 'CmsModel':              return project.CmsModel || '—';
    case 'CmsFirmware':           return project.CmsFirmware || '—';
    // STORED UNITS, LABELLED. The list carries the document's own Celsius and
    // metres; the per-project unit preference is applied server-side on the chart
    // endpoints, not here. A bare "70" would read as °F to an imperial user.
    case 'SamplingInterval':      return withUnit(project.SamplingInterval, 'm');
    case 'CuringTemperature':     return withUnit(project.CuringTemperature, '°C');
    case 'OverheatTemperature':   return withUnit(project.OverheatTemperature, '°C');
    case 'LinerLength':           return withUnit(project.LinerLength, 'm');
    default:                      return numberOrDash(p[columnId]);
  }
}

const numberOrDash = (value: unknown): string =>
  typeof value === 'number' && Number.isFinite(value) ? String(value) : '—';

const withUnit = (value: number | null, unit: string): string =>
  value === null ? '—' : `${value} ${unit}`;

/**
 * A stable CSS hook for one column: `col-<sanitised id>`.
 *
 * KEYED TO THE COLUMN ID, NEVER TO POSITION. :nth-child is the obvious way to style
 * "the third column" and it is wrong here: column order and visibility come from the
 * user's saved view, so a position-keyed rule styles a different column for every
 * person with a different view — and looks correct to whoever wrote it.
 *
 * User-defined fields carry free text in their id ("up:Liner Diameter/Thickness"), so
 * anything outside [A-Za-z0-9_-] collapses to a hyphen. Collapsing is lossy, so two
 * UDFs differing only in punctuation would share a class; that is a cosmetic
 * collision rather than a correctness one, and it is asserted rather than assumed.
 */
export function columnClass(columnId: string): string {
  return 'col-' + (columnId ?? '').replace(/[^A-Za-z0-9_-]+/g, '-').replace(/-+$/g, '');
}

/** A cell's sortable value: the raw one, not the formatted text. */
function sortValue(project: ProjectInfo, columnId: string): string | number | null {
  if (isUserFieldColumn(columnId)) {
    return (project.getUserParameterValue(userFieldName(columnId)) || '').toLowerCase();
  }
  const p = project as unknown as Record<string, unknown>;
  switch (columnId) {
    case 'ProjectName':           return (project.ProjectName || '').toLowerCase();
    // Sorted by what is DISPLAYED, so an unnamed device sorts among the named ones
    // by its serial rather than clumping under an empty string.
    case 'DeviceName':            return deviceLabel(project).toLowerCase();
    case 'SerialNumber':          return (project.SerialNumber || '').toLowerCase();
    case 'LastTelemetryReceived': return lastTelemetryEpoch(project);
    case 'CustomerName':          return (project.CustomerName || '').toLowerCase();
    case 'FiberType':             return (project.FiberType || '').toLowerCase();
    case 'Version':               return (project.Version || '').toLowerCase();
    case 'Status':                return project.isLive === true ? 1 : 0;
    case 'Finalized':             return project.Finalized === null ? null : project.Finalized ? 1 : 0;
    case 'CmsModel':              return (project.CmsModel || '').toLowerCase();
    case 'CmsFirmware':           return (project.CmsFirmware || '').toLowerCase();
    default: {
      const raw = p[columnId];
      return typeof raw === 'number' && Number.isFinite(raw) ? raw : null;
    }
  }
}

/**
 * Compare two projects on one column. NULLS SORT LAST IN BOTH DIRECTIONS — a
 * project with no value for the sorted column is not "smallest", it is unknown,
 * and reversing the sort should not march every unknown to the top.
 */
function compareOn(a: ProjectInfo, b: ProjectInfo, columnId: string, direction: 'asc' | 'desc'): number {
  const va = sortValue(a, columnId);
  const vb = sortValue(b, columnId);

  const aEmpty = va === null || va === '';
  const bEmpty = vb === null || vb === '';
  if (aEmpty && bEmpty) return 0;
  if (aEmpty) return 1;
  if (bEmpty) return -1;

  const sign = direction === 'asc' ? 1 : -1;
  if (typeof va === 'number' && typeof vb === 'number') return (va - vb) * sign;

  // TYPE-AWARE, not just type-inferred. Two values that are both numbers already
  // compare numerically above; this catches the case that used to sort wrongly —
  // a column whose values arrive as STRINGS that are really numbers, which is
  // every user-defined field, since a UDF value is always text on the wire.
  // Lexical order puts "10" before "9", which looks like a broken sort to anyone
  // reading a column of numbers.
  const na = numericOrNull(va);
  const nb = numericOrNull(vb);
  if (na !== null && nb !== null) return (na - nb) * sign;

  return String(va).localeCompare(String(vb), undefined, { numeric: true }) * sign;
}

/**
 * A value that is a number, or null if it is anything else.
 *
 * Whitespace and thousands separators are tolerated because a UDF is whatever
 * the operator typed on the desktop. An empty string is NOT zero — it has
 * already been handled as empty above, and treating it as 0 here would sort
 * blanks into the middle of the numbers.
 */
function numericOrNull(value: string | number): number | null {
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  const cleaned = value.trim().replace(/,/g, '');
  if (cleaned === '' || !/^-?\d*\.?\d+$/.test(cleaned)) return null;
  const n = Number(cleaned);
  return Number.isFinite(n) ? n : null;
}

/**
 * The distinct values a column actually holds, for a derived enum filter.
 *
 * BUILT FROM EVERY LOADED ROW, never from the filtered ones. Deriving from the
 * visible rows makes the chooser shrink as you use it: pick one fiber type and the
 * others disappear, so there is no way back except clearing the filter. That is
 * the failure that makes derived options feel broken, and it is one line apart
 * from the correct version.
 *
 * Sorted, and blanks dropped: an empty option reads as a value rather than as
 * "these rows have none".
 */
export function deriveEnumOptions(
  projects: readonly ProjectInfo[],
  columnId: string,
  dateFormat: DateFormat = DEFAULT_DATE_FORMAT,
  timeFormat: TimeFormat = DEFAULT_TIME_FORMAT
): ProjectGridEnumOption[] {
  const seen = new Set<string>();
  for (const project of projects ?? []) {
    // The CELL TEXT, because that is what the filter compares against — deriving
    // from the raw field would offer options that match nothing.
    const value = cellValue(project, columnId, dateFormat, timeFormat).trim();
    if (value && value !== '—') seen.add(value);
  }
  return [...seen].sort((a, b) => a.localeCompare(b, undefined, { numeric: true }))
    .map((value) => ({ value }));
}

/** Does this project pass the per-column filters? */
function passesFilters(project: ProjectInfo, view: ProjectGridView): boolean {
  for (const [columnId, filter] of Object.entries(view.filters ?? {})) {
    const text = cellValue(project, columnId).toLowerCase();
    switch (filter.op) {
      case 'equals':
        if (text !== (filter.value ?? '').toLowerCase()) return false;
        break;
      case 'in': {
        const allowed = (filter.values ?? []).map((v) => v.toLowerCase());
        if (allowed.length > 0 && !allowed.includes(text)) return false;
        break;
      }
      case 'between': {
        const epoch = lastTelemetryEpoch(project);
        if (epoch === null) break;   // unknown dates stay visible, as the date filter does
        if (filter.from && epoch < new Date(filter.from).getTime()) return false;
        if (filter.to) {
          const to = new Date(filter.to);
          to.setHours(23, 59, 59, 999);
          if (epoch > to.getTime()) return false;
        }
        break;
      }
      case 'contains':
      default:
        if (!text.includes((filter.value ?? '').toLowerCase())) return false;
        break;
    }
  }
  return true;
}

/** Passes the date range? A project with no timestamp stays VISIBLE. */
function passesDateRange(project: ProjectInfo, criteria: ProjectGridCriteria): boolean {
  if (!criteria.dateFrom && !criteria.dateTo) return true;

  // No/unparseable timestamp: keep the project visible instead of hiding it. A
  // brand-new cure may not have one stamped yet, and silently dropping it is how a
  // running cure vanishes from the pane entirely.
  const epoch = lastTelemetryEpoch(project);
  if (epoch === null) return true;

  if (criteria.dateFrom && epoch < new Date(criteria.dateFrom).getTime()) return false;
  if (criteria.dateTo) {
    const to = new Date(criteria.dateTo);
    to.setHours(23, 59, 59, 999);
    if (epoch > to.getTime()) return false;
  }
  return true;
}

/**
 * Build the whole rendered model in one pass.
 *
 * Sectioning: Live and Historical remain the outer split, because that is the
 * pane's meaning and it is server-decided. A user's own grouping nests INSIDE each
 * section rather than replacing it — a grid grouped by customer that mixed running
 * cures in with finished ones would lose the one distinction the pane exists to
 * make.
 */
export function buildProjectGridModel(
  projects: readonly ProjectInfo[],
  view: ProjectGridView,
  criteria: ProjectGridCriteria,
  haystacks?: ReadonlyMap<ProjectInfo, string>,
  dateFormat: DateFormat = DEFAULT_DATE_FORMAT,
  timeFormat: TimeFormat = DEFAULT_TIME_FORMAT
): ProjectGridModel {
  const all = projects ?? [];
  const available = availableColumns(all);
  const pruned = pruneToAvailableColumns(view, available);
  const byId = new Map(available.map((c) => [c.id, c]));
  const columns = pruned.columns
    .map((id) => byId.get(id) ?? builtInColumn(id))
    .filter((c): c is ProjectGridColumnDef => !!c);

  const matching = all.filter(
    (project) =>
      matchesSearch(haystacks?.get(project) ?? searchHaystack(project), criteria.searchTerm) &&
      passesDateRange(project, criteria) &&
      passesFilters(project, pruned)
  );

  const partitioned = partitionProjects(all);
  const { live, historical } = partitionProjects(matching);

  const wantLive = criteria.statusFilter !== 'historical';
  const wantHistorical = criteria.statusFilter !== 'live';

  const rows: ProjectGridRow[] = [];
  if (wantLive && live.length > 0) {
    rows.push({ kind: 'section', label: `Live Projects (${live.length})` });
    rows.push(...renderGroup(live, pruned, columns, dateFormat, timeFormat));
  }
  if (wantHistorical && historical.length > 0) {
    rows.push({ kind: 'section', label: `Historical Projects (${historical.length})` });
    rows.push(...renderGroup(historical, pruned, columns, dateFormat, timeFormat));
  }

  const filtered =
    (wantLive ? live.length : 0) + (wantHistorical ? historical.length : 0);

  return {
    columns,
    rows,
    counts: {
      total: all.length,
      live: partitioned.live.length,
      historical: partitioned.historical.length,
      filtered,
    },
  };
}

/** Order one section, insert group headings, and precompute every cell. */
function renderGroup(
  projects: readonly ProjectInfo[],
  view: ProjectGridView,
  columns: readonly ProjectGridColumnDef[],
  dateFormat: DateFormat,
  timeFormat: TimeFormat
): ProjectGridRow[] {
  const ordered = [...projects].sort((a, b) => compareRows(a, b, view));
  const rows: ProjectGridRow[] = [];

  let lastKey: string | null = null;
  for (const project of ordered) {
    if (view.groupBy.length > 0) {
      const key = view.groupBy.map((id) => cellValue(project, id, dateFormat, timeFormat)).join(' · ');
      if (key !== lastKey) {
        rows.push({ kind: 'section', label: key });
        lastKey = key;
      }
    }
    rows.push({
      kind: 'project',
      data: project,
      // The project name is this row's identity for trackBy. Computed here with
      // the rest of the row rather than derived in the grid, so the grid does not
      // need to know what kind of thing it is rendering.
      key: project.ProjectName,
      cells: columns.map((column) => cellValue(project, column.id, dateFormat, timeFormat)),
      standIn: columns.map((column) => isStandInCell(project, column.id)),
    });
  }
  return rows;
}

/**
 * Grouping columns order OUTERMOST, then the user's sort, then newest telemetry as
 * the final tie-break — so rows with equal sort keys keep the stable, meaningful
 * order the pane has always had rather than an arbitrary one.
 */
function compareRows(a: ProjectInfo, b: ProjectInfo, view: ProjectGridView): number {
  for (const columnId of view.groupBy) {
    const grouped = compareOn(a, b, columnId, 'asc');
    if (grouped !== 0) return grouped;
  }
  for (const sort of view.sort) {
    const sorted = compareOn(a, b, sort.columnId, sort.direction);
    if (sorted !== 0) return sorted;
  }
  return byNewestTelemetry(a, b);
}
